import * as React from "react";
import { makeStyles, shorthands } from "@fluentui/react-components";
import { Dismiss12Filled, Image24Regular } from "@fluentui/react-icons";
import { FONT_BODY } from "../schadenmeldung/theme";

// ---------------------------------------------------------------------------
// Shared state for photos picked DURING the wizard (before a referenceCode
// exists). The page collects File objects here and uploads them once the
// Schadenmeldung has been submitted. After submission the DoneStep uploads
// immediately via uploadSchadenmeldungFile().
// ---------------------------------------------------------------------------
interface PhotoCtx {
  files: File[];
  addFiles: (files: File[]) => void;
  removeFile: (index: number) => void;
}
const PhotoContext = React.createContext<PhotoCtx | null>(null);

export function PhotoProvider({
  value,
  children,
}: {
  value: PhotoCtx;
  children: React.ReactNode;
}) {
  return <PhotoContext.Provider value={value}>{children}</PhotoContext.Provider>;
}

export function usePhotos(): PhotoCtx {
  const ctx = React.useContext(PhotoContext);
  if (!ctx) {
    // Render-safe fallback (e.g. if used outside the provider).
    return { files: [], addFiles: () => {}, removeFile: () => {} };
  }
  return ctx;
}

const useStyles = makeStyles({
  wrap: { display: "flex", flexDirection: "column", gap: "10px" },
  dropzone: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    cursor: "pointer",
    textAlign: "center",
    color: "var(--sm-primary-deep)",
    fontFamily: FONT_BODY,
    fontSize: "0.9rem",
    fontWeight: 600,
    backgroundColor: "rgba(255,255,255,0.6)",
    ...shorthands.border("1.5px", "dashed", "var(--sm-accent)"),
    ...shorthands.borderRadius("12px"),
    paddingTop: "20px",
    paddingBottom: "20px",
    paddingLeft: "16px",
    paddingRight: "16px",
    ":hover": { backgroundColor: "rgba(247,168,35,0.10)" },
  },
  hint: { margin: 0, color: "#6c6171", fontSize: "0.82rem", fontWeight: 400, fontFamily: FONT_BODY },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))",
    gap: "10px",
  },
  thumb: {
    position: "relative",
    aspectRatio: "1 / 1",
    backgroundColor: "#f3f0f6",
    ...shorthands.border("1px", "solid", "var(--sm-card-border)"),
    ...shorthands.borderRadius("10px"),
    ...shorthands.overflow("hidden"),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  thumbImg: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
  thumbName: {
    fontSize: "0.7rem",
    color: "#6c6171",
    fontFamily: FONT_BODY,
    paddingLeft: "4px",
    paddingRight: "4px",
    textAlign: "center",
    wordBreak: "break-word",
  },
  remove: {
    position: "absolute",
    top: "4px",
    right: "4px",
    width: "22px",
    height: "22px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    backgroundColor: "rgba(0,0,0,0.55)",
    color: "#fff",
    ...shorthands.border("none"),
    ...shorthands.borderRadius("999px"),
  },
  status: { margin: 0, fontSize: "0.82rem", fontFamily: FONT_BODY, color: "#6c6171" },
});

function ThumbName({ file }: { file: File }) {
  const styles = useStyles();
  const isImage = file.type.startsWith("image/");
  const url = React.useMemo(() => (isImage ? URL.createObjectURL(file) : ""), [file, isImage]);
  React.useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);
  if (isImage) return <img className={styles.thumbImg} src={url} alt={file.name} />;
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
      <Image24Regular />
      <span className={styles.thumbName}>{file.name}</span>
    </div>
  );
}

const ACCEPT = "image/*,application/pdf,video/mp4,video/quicktime";

// Collects photos into the shared context (used during the wizard).
export function PhotoUpload({ hint }: { hint?: string }) {
  const styles = useStyles();
  const { files, addFiles, removeFile } = usePhotos();
  const inputRef = React.useRef<HTMLInputElement>(null);
  return (
    <div className={styles.wrap}>
      <div
        className={styles.dropzone}
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
      >
        <Image24Regular style={{ fontSize: 26 }} />
        <span>Fotos auswählen oder aufnehmen</span>
        {hint && <p className={styles.hint}>{hint}</p>}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        multiple
        style={{ display: "none" }}
        onChange={(e) => {
          if (e.target.files) addFiles(Array.from(e.target.files));
          e.target.value = "";
        }}
      />
      {files.length > 0 && (
        <div className={styles.grid}>
          {files.map((f, i) => (
            <div key={i} className={styles.thumb}>
              <ThumbName file={f} />
              <button
                type="button"
                className={styles.remove}
                aria-label={`${f.name} entfernen`}
                onClick={() => removeFile(i)}
              >
                <Dismiss12Filled />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Immediate uploader for the DoneStep — referenceCode is known, so each picked
// file is uploaded right away.
export function PhotoUploadImmediate({
  onUpload,
}: {
  onUpload: (file: File) => Promise<void>;
}) {
  const styles = useStyles();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [done, setDone] = React.useState<string[]>([]);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const handle = async (list: File[]) => {
    setBusy(true);
    setError(null);
    for (const f of list) {
      try {
        await onUpload(f);
        setDone((d) => [...d, f.name]);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Upload fehlgeschlagen");
      }
    }
    setBusy(false);
  };
  return (
    <div className={styles.wrap}>
      <div
        className={styles.dropzone}
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
      >
        <Image24Regular style={{ fontSize: 26 }} />
        <span>{busy ? "Wird hochgeladen …" : "Fotos / Unterlagen hochladen"}</span>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        multiple
        style={{ display: "none" }}
        onChange={(e) => {
          if (e.target.files) void handle(Array.from(e.target.files));
          e.target.value = "";
        }}
      />
      {done.length > 0 && (
        <p className={styles.status}>{done.length} Datei(en) hochgeladen: {done.join(", ")}</p>
      )}
      {error && <p className={styles.status} style={{ color: "#a23b3b" }}>{error}</p>}
    </div>
  );
}
